/**
 * Rate Limit Status Component
 * Stage 7: UI Enhancements & Loading States
 */

import React, { useState, useEffect } from 'react';
import { RateLimitStatus as RateLimitStatusType } from '../types/api';
import PoppingModal from './PoppingModal';
import { usePoppingModal } from '../hooks/usePoppingModal';

interface RateLimitStatusProps {
  rateLimitStatus?: RateLimitStatusType | null;
  isLoading?: boolean;
  className?: string;
  showUpgradePrompt?: boolean;
  compact?: boolean;
}

const RateLimitStatus: React.FC<RateLimitStatusProps> = ({
  rateLimitStatus,
  isLoading = false,
  className = '',
  showUpgradePrompt = true,
  compact = false
}) => {
  const { isOpen: isModalOpen, openModal: openUpgradeModal, closeModal: closeUpgradeModal } = usePoppingModal();
  const [timeUntilReset, setTimeUntilReset] = useState<string>('');

  // Calculate time until reset
  useEffect(() => {
    if (!rateLimitStatus?.resetDate) return;

    const calculateTimeUntilReset = () => {
      const resetTime = new Date(rateLimitStatus.resetDate);
      const now = new Date();
      const timeDiff = resetTime.getTime() - now.getTime();

      if (timeDiff <= 0) {
        setTimeUntilReset('Reset available');
        return;
      }

      const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));

      if (days > 0) {
        setTimeUntilReset(`${days}d ${hours}h`);
      } else if (hours > 0) {
        setTimeUntilReset(`${hours}h ${minutes}m`);
      } else {
        setTimeUntilReset(`${minutes}m`);
      }
    };

    calculateTimeUntilReset();
    const interval = setInterval(calculateTimeUntilReset, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [rateLimitStatus?.resetDate]);

  if (isLoading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className={`bg-gray-200 rounded-lg ${compact ? 'h-8 w-32' : 'h-12 w-48'}`}></div>
      </div>
    );
  }

  if (!rateLimitStatus) {
    return null;
  }

  const { remainingUses, /* usageCount, */ isBlocked } = rateLimitStatus;
  const totalUses = 15; // Weekly limit
  const usedUses = totalUses - remainingUses;

  // Determine status color and style based on percentage remaining
  const getStatusStyle = () => {
    const percentageRemaining = (remainingUses / totalUses) * 100;
    
    if (isBlocked || remainingUses === 0) {
      return {
        containerClass: 'bg-red-50 border-red-200 text-red-800',
        iconClass: 'text-red-500',
        progressClass: 'bg-red-500'
      };
    } else if (percentageRemaining <= 10) {
      // <10% remaining (0-1 uses)
      return {
        containerClass: 'bg-red-50 border-red-200 text-red-800',
        iconClass: 'text-red-500',
        progressClass: 'bg-red-500'
      };
    } else if (percentageRemaining <= 50) {
      // 10-50% remaining (2-7 uses)
      return {
        containerClass: 'bg-yellow-50 border-yellow-200 text-yellow-800',
        iconClass: 'text-yellow-500',
        progressClass: 'bg-yellow-500'
      };
    } else {
      // >50% remaining (>7 uses)
      return {
        containerClass: 'bg-green-50 border-green-200 text-green-800',
        iconClass: 'text-green-500',
        progressClass: 'bg-green-500'
      };
    }
  };

  const statusStyle = getStatusStyle();

  if (compact) {
    // Determine colors based on percentage remaining
    const percentageRemaining = (remainingUses / totalUses) * 100;
    let strokeColor = '#4481fd'; // Blue - good usage
    let centerColor = '#4481fd';
    
    if (remainingUses === 0) {
      strokeColor = '#ef4444'; // Red - no uses left
      centerColor = '#ef4444';
    } else if (percentageRemaining <= 10) {
      // <10% remaining (0-1 uses)
      strokeColor = '#ef4444'; // Red - low usage
      centerColor = '#ef4444';
    } else if (percentageRemaining <= 50) {
      // 10-50% remaining (2-7 uses)
      strokeColor = '#f59e0b'; // Orange - moderate usage
      centerColor = '#f59e0b';
    } else {
      // >50% remaining (>7 uses)
      strokeColor = '#4481fd'; // Blue - good usage
      centerColor = '#4481fd';
    }
    
    const circumference = 50.27; // 2 * π * r (r=8)
    const progressLength = (remainingUses / totalUses) * circumference;
    
    return (
      <div className={`inline-flex items-center gap-3 px-4 py-2 rounded-lg border bg-white border-gray-200 ${className}`}>
        <div className="flex items-center gap-2">
          <svg className="w-6 h-6 transform -rotate-90" viewBox="0 0 20 20">
            {/* Background circle */}
            <circle
              cx="10"
              cy="10"
              r="8"
              fill="none"
              stroke="rgba(37,54,88,0.15)"
              strokeWidth="1.5"
            />
            {/* Progress circle */}
            <circle
              cx="10"
              cy="10"
              r="8"
              fill="none"
              stroke={strokeColor}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeDasharray={`${progressLength} ${circumference}`}
              className="transition-all duration-500 ease-in-out"
            />
            {/* Center dot */}
            <circle
              cx="10"
              cy="10"
              r="1.5"
              fill={centerColor}
            />
          </svg>
          <span className="text-sm font-medium text-gray-700">
            {remainingUses}/{totalUses} left this week
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-4 rounded-lg border ${statusStyle.containerClass} ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <svg className={`w-5 h-5 ${statusStyle.iconClass}`} fill="currentColor" viewBox="0 0 20 20">
            {isBlocked ? (
              <path fillRule="evenodd" d="M13.477 14.89A6 6 0 015.11 6.524l8.367 8.368zm1.414-1.414L6.524 5.11a6 6 0 018.367 8.367zM18 10a8 8 0 11-16 0 8 8 0 0116 0z" clipRule="evenodd" />
            ) : (
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.293l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
            )}
          </svg>
          <h3 className="font-semibold">
            {isBlocked ? 'Rate Limit Reached' : 'Weekly Usage'}
          </h3>
        </div>
        <span className="text-lg font-bold">
          {remainingUses}/{totalUses}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span>Used: {usedUses}</span>
          <span>Remaining: {remainingUses}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${statusStyle.progressClass}`}
            style={{ width: `${(usedUses / totalUses) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Reset Information */}
      <div className="flex items-center justify-between text-sm">
        <span>Resets in:</span>
        <span className="font-medium">{timeUntilReset || 'Calculating...'}</span>
      </div>

      {/* Blocked State Message */}
      {isBlocked && (
        <div className="mt-3 p-3 bg-red-100 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800">
            You've reached your weekly limit of 15 avatar generations. 
            Your quota will reset in <strong>{timeUntilReset}</strong>.
          </p>
        </div>
      )}

      {/* Low Usage Warning */}
      {!isBlocked && remainingUses > 0 && remainingUses <= 2 && (
        <div className="mt-3 p-3 bg-yellow-100 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            You have <strong>{remainingUses} generation{remainingUses !== 1 ? 's' : ''} left</strong> this week. 
            Use {remainingUses === 1 ? 'it' : 'them'} wisely! Quota resets in <strong>{timeUntilReset}</strong>.
          </p>
        </div>
      )}

      {/* Upgrade Prompt */}
      {showUpgradePrompt && (isBlocked || remainingUses <= 2) && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Need more generations?</p>
              <p className="text-xs text-gray-600">Upgrade for unlimited access</p>
            </div>
            <button
              className="px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
              onClick={openUpgradeModal}
            >
              Upgrade
            </button>
          </div>
        </div>
      )}

      {/* Popping Modal for Upgrade */}
      <PoppingModal
        isOpen={isModalOpen}
        onClose={closeUpgradeModal}
        title="Unlock Unlimited Avatar Generations"
        subtitle="Subscribe to AI Pro to create unlimited custom avatars from BIGVU's global catalog. No more rate limits!"
        onSubscribe={() => {
          window.open('https://desk.bigvu.tv/welcome?utm_campaign=avatar_generator&utm_content=upgrade&utm_medium=rate_limit&utm_source=prompt2avatar', '_blank');
          closeUpgradeModal();
        }}
        onAlreadySubscribed={() => {
          window.open('https://desk.bigvu.tv/login?utm_campaign=avatar_generator&utm_content=already_subscribed&utm_medium=rate_limit&utm_source=prompt2avatar', '_blank');
          closeUpgradeModal();
        }}
        showTimeline={true}
      />
    </div>
  );
};

export default RateLimitStatus;
